import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [studentId, setStudentId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(() => localStorage.getItem('studentAvatar'));

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const storedStudentId = localStorage.getItem('studentId');
        
        if (token && storedStudentId) {
          // Verify token is still valid
          try {
            const profile = await apiService.getUserProfile();
            setUserProfile(profile);
            setStudentId(storedStudentId);
            setIsAuthenticated(true);
            setAvatarUrl(localStorage.getItem('studentAvatar'));
          } catch (error) {
            // Token is invalid, clear storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('studentId');
            setIsAuthenticated(false);
            setStudentId(null);
            setUserProfile(null);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        setStudentId(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);
      
      if (response.auth_token) {
        const profile = await apiService.getUserProfile();
        const studentProfile = profile.profile || profile.student_profile || {};
        const derivedStudentId = profile.student_id || studentProfile.student_id || studentProfile.id || profile.id;
        
        if (derivedStudentId) {
          setUserProfile(profile);
          setStudentId(derivedStudentId);
          localStorage.setItem('studentId', derivedStudentId);
          setAvatarUrl(localStorage.getItem('studentAvatar'));
          setIsAuthenticated(true);
          return { success: true, studentId: derivedStudentId };
        } else {
          throw new Error('Student account not found');
        }
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      setIsAuthenticated(false);
      setStudentId(null);
      setUserProfile(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('studentId');
      localStorage.removeItem('studentAvatar');
      setAvatarUrl(null);
    }
  };

  const refreshProfile = async () => {
    try {
      const profile = await apiService.getUserProfile();
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('Profile refresh failed:', error);
      throw error;
    }
  };

  const updateAvatar = (dataUrl) => {
    try {
      if (dataUrl) {
        localStorage.setItem('studentAvatar', dataUrl);
        setAvatarUrl(dataUrl);
      } else {
        localStorage.removeItem('studentAvatar');
        setAvatarUrl(null);
      }
    } catch (error) {
      console.error('Failed to update avatar:', error);
    }
  };

  const value = {
    isAuthenticated,
    studentId,
    userProfile,
    loading,
    login,
    logout,
    refreshProfile,
    avatarUrl,
    updateAvatar
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
